/**
 * Admin-Ansicht: alle Konten (seit 16.10.2026). Nur für Konten in
 * SIM_ADMIN_KONTEN; für alle anderen antwortet der Server mit 404, und die
 * Seite sagt nur, dass es hier nichts gibt. Der Reiter „Admin“ erscheint nur
 * für Admins (ui.tsx, AppRahmen).
 *
 * Zeigt Zählungen, keine Inhalte: was geprüft wurde, steht nicht hier
 * (admin.ts, Kopfkommentar). Ein Klick auf ein Konto öffnet das Detail mit dem
 * Knopf zum Löschen.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { ApiFehler, ladeAdminKonten, type AdminKonto } from '../api';
import { useSitzung } from '../session';
import { Abschnitt, AppRahmen, Hinweis, Kennzahl, KleinerKnopf, Marke, Panel, Platzhalter, Seitenkopf, Zeile } from '../ui';
import { datum, filtereKonten, kennzeichen, mehrzahl, zahl } from '../admin-anzeige';
import { t } from '../sprache';

export default function Admin() {
  const { session, abmelden } = useSitzung();
  const [konten, setKonten] = useState<AdminKonto[] | null>(null);
  const [fehler, setFehler] = useState('');
  const [keinZugang, setKeinZugang] = useState(false);
  const [laedt, setLaedt] = useState(true);
  const [suche, setSuche] = useState('');
  const [params] = useSearchParams();
  const geloescht = params.get('geloescht');

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      setKonten(await ladeAdminKonten());
    } catch (e) {
      const f = e as ApiFehler;
      if (f.status === 404) setKeinZugang(true);
      else setFehler(f.message);
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [abmelden]);

  useEffect(() => { void holen(); }, [holen]);
  // Nach dem Löschen kommt man von unten auf der Detailseite: nach oben, zur Bestätigung.
  useEffect(() => { if (geloescht) window.scrollTo(0, 0); }, [geloescht]);

  const sichtbar = useMemo(() => filtereKonten(konten ?? [], suche), [konten, suche]);
  const vor30 = Date.now() - 30 * 86_400_000;
  const aktiv = (konten ?? []).filter(k => k.letzter_run && new Date(k.letzter_run).getTime() > vor30).length;

  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <Seitenkopf titel={t('Konten', 'Accounts')} unter={t('Alle Konten von 99takes. Zählungen und Abrechnung, keine Inhalte.', 'All 99takes accounts. Counts and billing, no content.')} />

      {keinZugang && <Hinweis art="schlecht">{t('Diese Seite gibt es für dein Konto nicht.', 'This page is not available for your account.')}</Hinweis>}
      {geloescht && <Hinweis art="gut">{t(<>Konto {geloescht} ist gelöscht.</>, <>Account {geloescht} is deleted.</>)}</Hinweis>}
      {fehler && <Hinweis art="schlecht">{fehler} <button type="button" onClick={() => void holen()} className="cursor-pointer border-0 bg-transparent p-0 font-medium text-current underline underline-offset-[3px]">{t('Noch einmal laden', 'Load again')}</button></Hinweis>}

      {!keinZugang && (
        <>
          {konten && (
            <Panel className="mb-10 grid grid-cols-2 gap-5 px-4 py-5 sm:grid-cols-4 sm:px-6">
              <Kennzahl label={t('Konten', 'Accounts')} wert={zahl(konten.length)} />
              <Kennzahl label={t('mit Anmeldung', 'with sign-in')} wert={zahl(konten.filter(k => k.anmeldung).length)} />
              <Kennzahl label={t('Run in 30 Tagen', 'Run in 30 days')} wert={zahl(aktiv)} />
              <Kennzahl label={t('mit Abo', 'with subscription')} wert={zahl(konten.filter(k => k.abo?.status && k.abo.status !== 'canceled').length)} />
            </Panel>
          )}

          <Abschnitt
            titel={t('Alle Konten', 'All accounts')}
            zahl={konten ? sichtbar.length : undefined}
            aktion={<KleinerKnopf type="button" onClick={() => void holen()} disabled={laedt}>{laedt ? t('Lädt …', 'Loading …') : t('Neu laden', 'Reload')}</KleinerKnopf>}
          >
            <label className="mb-3 block">
              <span className="sr-only">{t('Konten durchsuchen', 'Search accounts')}</span>
              <input
                type="search" value={suche} onChange={e => setSuche(e.target.value)} placeholder={t('Adresse, Name oder Konto-id', 'Address, name or account id')}
                className="block h-11 w-full max-w-md rounded-[0.625rem] border border-linie-stark bg-flaeche px-3 text-[1rem] text-tinte focus:border-marke focus:outline-none"
              />
            </label>
            {laedt && !konten && <Panel>{[0, 1, 2].map(i => <Zeile key={i}><Platzhalter zeilen={2} /></Zeile>)}</Panel>}
            {konten && sichtbar.length === 0 && (
              <Panel className="px-4 py-5 text-[0.9375rem] text-text-leise sm:px-5">{t(<>Kein Konto passt zu „{suche}“.</>, <>No account matches “{suche}”.</>)}</Panel>
            )}
            {sichtbar.length > 0 && <Panel>{sichtbar.map(k => <KontoZeile key={k.id} k={k} />)}</Panel>}
          </Abschnitt>
        </>
      )}
    </AppRahmen>
  );
}

function KontoZeile({ k }: { k: AdminKonto }) {
  const teile = [
    t(`seit ${datum(k.erstellt)}`, `since ${datum(k.erstellt)}`),
    mehrzahl(k.runs, t('Run', 'run'), t('Runs', 'runs')),
    k.letzter_run ? t(`zuletzt ${datum(k.letzter_run)}`, `last ${datum(k.letzter_run)}`) : null,
    k.zielgruppen ? mehrzahl(k.zielgruppen, t('Zielgruppe', 'audience'), t('Zielgruppen', 'audiences')) : null,
    k.berichte ? mehrzahl(k.berichte, t('Bericht', 'report'), t('Berichte', 'reports')) : null,
    typeof k.guthaben === 'number' ? `${zahl(k.guthaben)} Credits` : null,
  ].filter(Boolean);
  return (
    <div className="border-t border-linie-zart first:border-t-0">
      <Link to={`/admin/${encodeURIComponent(k.id)}`} className="block px-4 py-3.5 no-underline first:rounded-t-karte hover:bg-ebene sm:px-5">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
          <span className="min-w-0 text-[0.9375rem] font-semibold [overflow-wrap:anywhere] text-tinte">{k.email || k.name || k.id}</span>
          {kennzeichen(k).map(z => <Marke key={z.text} art={z.art}>{z.text}</Marke>)}
        </span>
        <span className="mt-1 block text-[0.8125rem] text-text-hell tabular-nums">
          <span className="font-mono">{k.id}</span> · {teile.join(' · ')}
        </span>
      </Link>
    </div>
  );
}
